import React, { useEffect, useRef, useState } from "react";
import secureLocalStorage from "react-secure-storage";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import ReactCardFlip from "react-card-flip";
import { Button, Card, Rate, Tooltip } from "antd";
import parse from "html-react-parser";
import { FaAngleDoubleRight, FaHeart } from "react-icons/fa";
import {
  TbHexagonLetterC,
  TbHexagonLetterP,
  TbHexagonLetterR,
  TbHexagonLetterS,
  TbHexagonLetterW,
  TbSquareLetterC,
  TbHexagonLetterCFilled,
  TbSquareLetterG,
  TbSquareLetterM,
  TbChevronCompactDown,
} from "react-icons/tb";
import hostURL, {
  allCoverTypes,
  allPriorities,
} from "../../../Common/constants";
import GCRPImage from "../../../images/practices/GCRP.png";
import "../style.css";
import { nextStep } from "../../../Redux/tutorialSlice";
import useTutorialSteps from "../../Tutorial/useTutorialSteps";

const PracticeCard = ({
  mode,
  listView,
  practice,
  preferredPractice,
  userFavoritePractices,
  favOnchange,
  isFirstCard,
  selected,
  printView,
  disableInteractions,
  crepColorMap,
  ...rest
}) => {
  const selectedFips = useSelector((state) => state.filter.selectedFips);
  const currentStep = useSelector((state) => state.tutorial.currentStep);
  const dispatch = useDispatch();
  const [cardClass, setCardClass] = useState("");
  const crepLinkClickedRef = useRef(false);
  const isLoggedIn = secureLocalStorage.getItem("token");
  const fieldDetails = secureLocalStorage.getItem("field-details");
  const crepCodes = {
    "NORTH CENTRAL IOWA CREP": "DML",
    "ILLINOIS RIVER CREP": "ILC",
    "KASKASKIA RIVER CREP": "ILC",
    "INDIANA CREP": "INC",
    "MISSISSIPPI DELTA CREP": "MD",
    "LAKE ERIE CREP": "LEW",
    "SCIOTO RIVER CREP": "SRW",
  };
  const { handleNextStep } = useTutorialSteps();

  useEffect(() => {
    setCardClass(classNameSelector());
  }, [listView, preferredPractice, userFavoritePractices]);

  const practiceTitle = practice.title?.split("-")[0]?.trim();

  const topRibbons = [];

  // Only filter out field specific CREP under check eligibility mode
  if (Array.isArray(practice.crepInfo)) {
    if (mode === "check_eligibility" && fieldDetails.crepArea) {
      practice.crepInfo
        .filter((crep) =>
          fieldDetails.crepArea.rows
            .map((item) => item.code)
            .includes(crepCodes[crep.crepname])
        )
        .forEach((item) => {
          topRibbons.push({
            label: item.crepname,
            color: crepColorMap?.get(item.crepname) || "#888",
            type: "crep",
          });
        });
    } else {
      practice.crepInfo.forEach((item) => {
        topRibbons.push({
          label: item.crepname,
          color: crepColorMap?.get(item.crepname) || "#888",
          type: "crep",
        });
      });
    }
  }

  if (practice.isMostCommon) {
    topRibbons.unshift({
      label: "Most Common",
      color: "#307d00",
      type: "most-common",
    });
  }

  const matchingCreps = Array.isArray(practice.crepInfo)
    ? practice.crepInfo
    : [];

  const classNameSelector = () => {
    let myClassName = "practice-card";
    if (listView === "List") {
      myClassName += " list-view";
      if (selected) myClassName += " selected";
    } else if (listView === "Grid") {
      if (selected) myClassName += " selected";
      myClassName += " practice-card-vertical";
    } else {
      if (selected) myClassName += " selected";
      myClassName += " practice-card-horizontal";
    }
    return myClassName;
  };

  const incentiveConfig = [
    {
      key: "sip",
      title: "Signup Incentive – One-time payment",
      icon: <TbHexagonLetterS size="2em" color="maroon" />,
      dynamic: true,
    },
    {
      key: "rentalRateIncentive",
      title: "Rental Rate Incentive – Annual payment",
      icon: <TbHexagonLetterR size="2em" color="maroon" />,
      dynamic: true,
    },
    {
      key: "pip",
      title: "Practice Incentive – One-time payment",
      icon: <TbHexagonLetterP size="2em" color="maroon" />,
      dynamic: true,
    },
    {
      key: "waterqualitythruclear30",
      title: "Water Quality Bonus – CLEAR30",
      icon: <TbHexagonLetterW size="2em" color="maroon" />,
      dynamic: true,
    },
  ];

  const incentiveMap = practice.programs?.reduce((acc, program) => {
    Object.entries(program.incentives || {}).forEach(([key, value]) => {
      if (value && value !== "false" && value !== "NA" && value !== "0") {
        acc[key] = value;
      }
    });
    return acc;
  }, {});

  const [isFlipped, setIsFlipped] = useState(false);

  const hasCreps =
    Array.isArray(practice.crepInfo) && practice.crepInfo.length > 0;

  const handleCardClick = () => {
    if (disableInteractions) return;

    if (crepLinkClickedRef.current) {
      crepLinkClickedRef.current = false; // reset for next click
      return;
    }

    const isTutorialStep = currentStep === 22;

    if (hasCreps) {
      setIsFlipped((prev) => {
        const next = !prev;

        if (isTutorialStep && next === true) {
          setTimeout(() => {
            handleNextStep();
          }, 400); // let flip animation complete
        }

        return next;
      });
    } else {
      const url = `/Practice/${selectedFips}/${btoa(practice.pdfLink)}/${btoa(
        practice.title
      )}`;
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const isFieldReport = useLocation().pathname === "/FieldReport";

  return (
    <div>
      {
        <ReactCardFlip
          isFlipped={hasCreps && isFlipped}
          flipDirection="horizontal"
        >
          <Card
            className={
              cardClass +
              (isFirstCard ? " practice-card-tutorial" : "") +
              (practice.isMostCommon ? " tutorial-most-common-card" : "") +
              (hasCreps ? " tutorial-crep-card" : "")
            }
            data-practice-title={practice.title}
            title={
              <>
                <p className="practice-number ">
                  <span className="tutorial-practice-number">
                    {practice.title?.split("-")[0]}
                  </span>
                  <div style={{ float: "right" }}>
                    {practice.scoreLabel && (
                      <span
                        className={`${practice.scoreClassName} tutorial-practice-match`}
                      >
                        {practice.scoreLabel}
                      </span>
                    )}
                    {isLoggedIn !== null && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation(); // prevents card flip
                        }}
                      >
                        <Rate
                          character={
                            <FaHeart
                              color={
                                userFavoritePractices.some(
                                  (fav) => fav.trim() === practice.title?.trim()
                                )
                                  ? "coral"
                                  : "lightgray"
                              }
                              style={{ verticalAlign: "middle" }}
                            />
                          }
                          count="1"
                          value={
                            userFavoritePractices.some(
                              (fav) => fav.trim() === practice.title?.trim()
                            )
                              ? 1
                              : 0
                          }
                          onChange={(val) =>
                            favOnchange(
                              val,
                              practice.title + "#" + practice.pdfLink
                            )
                          }
                        />
                      </span>
                    )}
                  </div>
                </p>
                <p className="practice-title tutorial-practice-title">
                  {practice.title?.split(/-(.*)/)[1]}
                </p>
              </>
            }
            headStyle={{
              backgroundColor: "#5D1725",
              color: "white",
            }}
            onClick={handleCardClick}
            key={"front"}
          >
            <div
              className={
                cardClass.includes("practice-card-vertical")
                  ? ""
                  : "flag-container-horizontal"
              }
            >
              {[...topRibbons].map((ribbon, index) => (
                <div
                  key={index}
                  className={`${
                    cardClass.includes("practice-card-vertical")
                      ? "practice-flag-vertical"
                      : "practice-flag-horizontal"
                  } ${
                    ribbon.type === "most-common"
                      ? "practice-flag-most-common"
                      : ""
                  }`}
                  style={{
                    top: cardClass.includes("practice-card-vertical")
                      ? `${110 + index * 30}px`
                      : `0px`,
                    backgroundColor: ribbon.color,
                    zIndex: 10 + index,
                  }}
                >
                  <p className="crep-flag">{ribbon.label}</p>
                </div>
              ))}
            </div>

            {/* LAND COVER ICONS */}
            {practice.landcover && (
              <p className="step2_images tutorial-practice-cover">
                <b>Land Cover:</b>
                <br />
                {allCoverTypes
                  .filter(({ value }) => practice.landcover.includes(value))
                  .map(({ value, label, src }) => (
                    <Tooltip key={value} placement="topLeft" title={label}>
                      <img className="img_selected_cb" src={src} alt={value} />
                    </Tooltip>
                  ))}
              </p>
            )}

            {/* PRIORITY ICONS */}
            {practice.priorities && (
              <p className="step3_images tutorial-practice-priorities">
                <b>Priorities:</b>
                <br />
                {practice.priorities.length > 0 ? (
                  practice.priorities.map(({ name }) => {
                    const item = allPriorities.find((p) => p.value === name);
                    return (
                      item && (
                        <Tooltip
                          key={name}
                          placement="topLeft"
                          title={item.label}
                        >
                          <img
                            className="img_selected_cb"
                            src={item.src}
                            alt={name}
                          />
                        </Tooltip>
                      )
                    );
                  })
                ) : (
                  <span>No priorities selected.</span>
                )}
                {practice.priorities.length > 3 && !printView && (
                  <div className="scroll-indicator">
                    <TbChevronCompactDown />
                  </div>
                )}
              </p>
            )}

            {(practice.general || practice.continuous) && (
              <p className="signup-icons tutorial-practice-signup">
                <b>Signups: </b>
                <br />
                {practice.general && !practiceTitle.includes("88") && (
                  <Tooltip title="Available In General Signup">
                    <TbSquareLetterG size="2em" color="maroon" />
                  </Tooltip>
                )}
                {practice.continuous && !practiceTitle.includes("88") && (
                  <Tooltip title="Available In Continuous Signup">
                    <TbSquareLetterC size="2em" color="maroon" />
                  </Tooltip>
                )}
                {practice.continuous && practiceTitle.includes("88") && (
                  <Tooltip title="Available In Grassland CRP">
                    <img src={GCRPImage} alt="GCRP Image" height={40} />
                  </Tooltip>
                )}
              </p>
            )}

            {Object.keys(incentiveMap || {}).length > 0 && (
              <div className="incentive-icons tutorial-practice-incentives">
                <b>Incentives: </b>
                <div className="incentive-icons-inner">
                  {incentiveConfig.map(
                    ({ key, title, icon, percent, dynamic }) => {
                      const value = incentiveMap[key];

                      if (
                        [
                          "climate_smrt_incentive",
                          "climatesmartincentivethrucontinous",
                        ].includes(key) &&
                        key !==
                          (incentiveMap["climate_smrt_incentive"]
                            ? "climate_smrt_incentive"
                            : "climatesmartincentivethrucontinous")
                      ) {
                        return null;
                      }

                      if (dynamic) {
                        if (!value || value === "false" || value === "NA")
                          return null;
                      } else if (!value) {
                        return null;
                      }

                      return (
                        <Tooltip key={key} title={title}>
                          {icon}
                          <b className="incentive-percent">
                            {dynamic ? `${value}%` : percent}
                          </b>
                        </Tooltip>
                      );
                    }
                  )}
                </div>
              </div>
            )}

            {!isFieldReport && disableInteractions ? (
              <p className={"detail-right"}>
                <p className="detail-button tutorial-practice-details">
                  <a
                    href={`/Practice/${selectedFips}/${btoa(
                      practice.pdfLink
                    )}/${btoa(practice.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "maroon", fontWeight: "bold" }}
                  >
                    Details &raquo;
                  </a>
                </p>
              </p>
            ) : (
              !isFieldReport && (
                <p className={"detail-right"}>
                  <p className="detail-button tutorial-practice-details">
                    <b>Details</b> <FaAngleDoubleRight />
                  </p>
                </p>
              )
            )}
          </Card>

          <Card
            onClick={handleCardClick}
            key={"back"}
            headStyle={{
              backgroundColor: "#5D1725",
              color: "white",
            }}
            className={
              cardClass +
              (isFirstCard ? " practice-card-tutorial" : "") +
              " practice-card-back"
            }
            title={
              <>
                <p className="practice-number">
                  {practice.title?.split("-")[0]}
                  <div style={{ float: "right" }}>
                    {practice.scoreLabel && (
                      <span className={practice.scoreClassName}>
                        {practice.scoreLabel}
                      </span>
                    )}
                  </div>
                </p>
                <p className="practice-title">
                  {practice.title?.split(/-(.*)/)[1]}
                </p>
              </>
            }
          >
            <div
              className={`practice-card-back-content${
                hasCreps ? " tutorial-crep-back-content" : ""
              }`}
            >
              <h3 className="crep-section-heading">CREP Options</h3>

              {matchingCreps.map((item, idx) => (
                <div key={idx} className="crep-detail-block">
                  <p>
                    <strong>{item.crepname}</strong> is an option available for
                    this practice.
                  </p>
                  <p>
                    <a
                      href={`/Practice/${selectedFips}/${btoa(
                        practice.pdfLink
                      )}/${btoa(practice.title)}/${btoa(item.crepname)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        crepLinkClickedRef.current = true;
                      }}
                    >
                      View {item.crepname} details &raquo;
                    </a>
                  </p>
                </div>
              ))}
              <hr className="crep-divider" />

              <p className="practice-detail-text">
                Want to explore <strong>{practice.title}</strong>?{" "}
                <a
                  href={`/Practice/${selectedFips}/${btoa(
                    practice.pdfLink
                  )}/${btoa(practice.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    crepLinkClickedRef.current = true;
                  }}
                >
                  Open practice details &raquo;
                </a>
              </p>
            </div>
          </Card>
        </ReactCardFlip>
      }
    </div>
  );
};
export default PracticeCard;
